// Slice 40 — modal with backdrop blur + cyan-tinted box-shadow. Closes on
// outer-click and Escape; ⌘N etc. are wired at the shell level.

import { useEffect } from "react";
import type { ReactNode } from "react";
import { HqIconButton } from "./HqButton";
import { Icons } from "./icons";

export interface HqModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  width?: number;
}

export function HqModal({ open, onClose, title, children, footer, width = 520 }: HqModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        display: "grid",
        placeItems: "center",
        background: "rgba(2,4,8,.65)",
        backdropFilter: "blur(4px)",
      }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="hq-fade-in"
        style={{
          width,
          maxWidth: "92%",
          maxHeight: "92%",
          overflow: "auto",
          background: "var(--hq-bg-raised)",
          borderRadius: "var(--hq-radius-2xl)",
          border: "1px solid var(--hq-border-strong)",
          boxShadow: "0 30px 80px rgba(0,0,0,.6), 0 0 0 1px rgba(34,211,238,.06)",
        }}
      >
        {title && (
          <div
            style={{
              padding: "14px 18px",
              borderBottom: "1px solid var(--hq-border)",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 600, color: "var(--hq-text)", flex: 1 }}>
              {title}
            </span>
            <HqIconButton icon={Icons.X} size="sm" onClick={onClose} title="Close" />
          </div>
        )}
        <div style={{ padding: 18 }}>{children}</div>
        {footer && (
          <div
            style={{
              padding: "12px 18px",
              borderTop: "1px solid var(--hq-border)",
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
